import {
  AUTH_API_BASE_URL,
  AUTH_STORAGE_KEYS,
} from './config';
import type { ApiEnvelope } from './client';
import { getOrCreateDeviceFingerprint } from '@/lib/auth/device';
import { getOrFetchClientPublicIp } from '@/lib/auth/clientPublicIp';
import { hardLogout } from '@/lib/auth/session-end';

export class AuthApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: ApiEnvelope<unknown>,
  ) {
    super(message);
    this.name = 'AuthApiError';
  }
}

type AuthRequestOptions = RequestInit & {
  auth?: boolean;
  sessionHeader?: boolean;
  /** Override base (e.g. Central Hub `API_BASE_URL` for `/auth/sessions` while login uses Auth Service). */
  baseUrl?: string;
};

let refreshPromise: Promise<string | null> | null = null;

type AccessTokenRefreshListener = (accessToken: string) => void;
const accessTokenRefreshListeners = new Set<AccessTokenRefreshListener>();

/** Register to sync permissions/UI after silent token refresh. */
export function onAccessTokenRefreshed(listener: AccessTokenRefreshListener): () => void {
  accessTokenRefreshListeners.add(listener);
  return () => accessTokenRefreshListeners.delete(listener);
}

function attachDeviceFingerprint(headers: Headers): void {
  try {
    const fp = getOrCreateDeviceFingerprint();
    if (fp) headers.set('X-Device-Fingerprint', fp);
  } catch {
    /* ignore */
  }
}

/** Fingerprint + optional egress IP hint for auth (see `clientPublicIp.ts`). */
async function attachAuthClientHints(headers: Headers): Promise<void> {
  attachDeviceFingerprint(headers);
  try {
    const pub = await getOrFetchClientPublicIp();
    if (pub) headers.set('X-Client-Public-IP', pub);
  } catch {
    /* ignore */
  }
}

function getAccessToken(): string | null {
  return localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
}

function getSessionId(): string | null {
  return localStorage.getItem(AUTH_STORAGE_KEYS.sessionId);
}

export async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const storedRefresh = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
      const body =
        storedRefresh != null && storedRefresh.length > 0
          ? JSON.stringify({ refreshToken: storedRefresh })
          : JSON.stringify({});

      const refreshHeaders = new Headers({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      });
      await attachAuthClientHints(refreshHeaders);

      const response = await fetch(`${AUTH_API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: refreshHeaders,
        body,
      });

      let bodyJson:
        | ApiEnvelope<{
            accessToken: string;
            refreshToken?: string;
            sessionId?: string;
            deviceName?: string | null;
          }>
        | undefined;
      try {
        bodyJson = (await response.json()) as ApiEnvelope<{
          accessToken: string;
          refreshToken?: string;
          sessionId?: string;
          deviceName?: string | null;
        }>;
      } catch {
        bodyJson = undefined;
      }

      if (!response.ok || !bodyJson?.data?.accessToken) {
        return null;
      }

      localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, bodyJson.data.accessToken);
      if (bodyJson.data.refreshToken) {
        localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, bodyJson.data.refreshToken);
      }
      if (bodyJson.data.sessionId) {
        localStorage.setItem(AUTH_STORAGE_KEYS.sessionId, bodyJson.data.sessionId);
      }
      if (bodyJson.data.deviceName != null && String(bodyJson.data.deviceName).length > 0) {
        localStorage.setItem(AUTH_STORAGE_KEYS.deviceName, String(bodyJson.data.deviceName));
      }
      for (const listener of accessTokenRefreshListeners) {
        try {
          listener(bodyJson.data.accessToken);
        } catch {
          /* non-fatal */
        }
      }
      return bodyJson.data.accessToken;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function authRequest<T>(
  path: string,
  options: AuthRequestOptions = {},
): Promise<ApiEnvelope<T>> {
  const {
    auth = false,
    sessionHeader = false,
    baseUrl,
    headers: initHeaders,
    ...init
  } = options;

  const headers = new Headers(initHeaders);
  headers.set('Accept', 'application/json');
  await attachAuthClientHints(headers);
  if (!headers.has('Content-Type') && init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  if (auth) {
    const token = getAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (sessionHeader) {
      const sessionId = getSessionId();
      if (sessionId) headers.set('x-session-id', sessionId);
    }
  }

  const origin = baseUrl ?? AUTH_API_BASE_URL;
  const url = path.startsWith('http') ? path : `${origin}${path}`;

  const doFetch = () =>
    fetch(url, {
      ...init,
      headers,
      credentials: 'include',
    });

  let response = await doFetch();

  const shouldRefresh =
    auth &&
    (response.status === 401 ||
      response.status === 403);

  if (shouldRefresh) {
    let bodyPeek: ApiEnvelope<T> | undefined;
    try {
      bodyPeek = (await response.clone().json()) as ApiEnvelope<T>;
    } catch {
      bodyPeek = undefined;
    }
    const code = bodyPeek?.code;
    if (code === 'SESSION_REVOKED') {
      hardLogout('session-ended');
    } else {
      const stale = response.status === 403 && code === 'SESSION_STALE';
      if (response.status === 401 || stale) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          headers.set('Authorization', `Bearer ${newToken}`);
          response = await doFetch();
        } else {
          hardLogout('session-ended');
        }
      }
    }
  }

  let body: ApiEnvelope<T> | undefined;
  try {
    body = (await response.json()) as ApiEnvelope<T>;
  } catch {
    body = undefined;
  }

  if (!response.ok || body?.success === false) {
    throw new AuthApiError(
      body?.error || body?.message || `Request failed (${response.status})`,
      response.status,
      body,
    );
  }

  return body ?? { success: true };
}

export async function authUploadFormData<T>(
  path: string,
  formData: FormData,
): Promise<ApiEnvelope<T>> {
  return authRequest<T>(path, {
    method: 'PATCH',
    auth: true,
    body: formData,
  });
}

export function clearAuthStorage(): void {
  localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
  localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
  localStorage.removeItem(AUTH_STORAGE_KEYS.sessionId);
  localStorage.removeItem(AUTH_STORAGE_KEYS.deviceName);
}
